import { useEffect, useRef, useState } from 'react';

import Btn from './Btn';
import { Icon } from './Icon';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * ConfirmModal — branded replacement for window.confirm() / window.prompt().
 *
 * Two modes:
 *   1. Confirm: just title + message + Confirm/Cancel buttons.
 *   2. Input mode: same shell + a textarea. Pass `inputLabel` (string) to
 *      activate. Submit returns the entered text via onConfirm(input).
 *
 * Usage: normally consumed through useConfirm() from lib/confirm.jsx — that
 * gives a Promise-based API that mirrors window.confirm/prompt. This file
 * is just the presentation layer.
 *
 * Props:
 *   open                 — boolean, mount/show
 *   title                — string (optional headline)
 *   message              — string or node, the body copy
 *   confirmLabel         — default 'Confirm'
 *   cancelLabel          — default 'Cancel'
 *   confirmVariant       — 'primary' (default), 'danger', 'navy', etc.
 *                          (any Btn variant — typically 'danger' for delete flows)
 *   inputLabel           — if set, renders a textarea above the buttons
 *   inputPlaceholder     — placeholder for the textarea
 *   inputRequired        — if true, Confirm is disabled until input has content
 *   onConfirm(value?)    — called on Confirm. value is the input string when in input mode.
 *   onCancel()           — called on Cancel / ESC / backdrop click
 */
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  inputLabel,
  inputPlaceholder,
  inputRequired = false,
  onConfirm,
  onCancel,
}) {
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState('');
  const confirmBtnRef = useRef(null);
  const inputRef = useRef(null);
  const inputMode = !!inputLabel;

  // Reset input on each open and focus the right control
  useEffect(() => {
    if (!open) return;
    setInputValue('');
    const t = setTimeout(() => {
      if (inputMode) inputRef.current?.focus();
      else confirmBtnRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open, inputMode]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      // Cmd/Ctrl+Enter submits in input mode
      if (inputMode && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (!inputRequired || inputValue.trim()) onConfirm?.(inputValue);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, inputMode, inputValue, inputRequired, onConfirm, onCancel]);

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const confirmDisabled = inputMode && inputRequired && !inputValue.trim();

  const overlayStyle = {
    position: 'fixed', inset: 0,
    background: 'rgba(10, 15, 46, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
    zIndex: 1100,
    padding: isMobile ? 0 : 20,
    animation: 'fadeIn 0.2s ease',
  };

  const cardStyle = isMobile
    ? {
        background: 'var(--surface)',
        borderTopLeftRadius: 'var(--r-lg)',
        borderTopRightRadius: 'var(--r-lg)',
        width: '100%',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border)',
        padding: '24px 20px 28px',
        boxSizing: 'border-box',
        maxHeight: '90dvh',
        overflowY: 'auto',
      }
    : {
        background: 'var(--surface)',
        borderRadius: 'var(--r-lg)',
        maxWidth: 440, width: '100%',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--border)',
        padding: '28px 28px 24px',
        boxSizing: 'border-box',
      };

  // Stop click propagation so backdrop click only fires when target IS the
  // overlay itself, not a click that bubbled up from inside the card.
  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) onCancel?.();
  };

  // Heuristic: destructive variants get a red icon glyph at top
  const isDestructive = confirmVariant === 'danger';

  return (
    <div onClick={onOverlayClick} role="dialog" aria-modal="true" aria-labelledby="cm-title" style={overlayStyle}>
      <div style={cardStyle}>
        {/* Optional icon — only for destructive flows */}
        {isDestructive && (
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 14,
          }}>
            <Icon.alert size={22} />
          </div>
        )}

        {title && (
          <h2 id="cm-title" style={{
            fontSize: 18, fontWeight: 800,
            letterSpacing: '-0.01em',
            color: 'var(--text)',
            margin: 0, marginBottom: 8,
          }}>
            {title}
          </h2>
        )}

        {message && (
          <div style={{
            fontSize: 13.5, color: 'var(--text-2)',
            lineHeight: 1.55,
            marginBottom: inputMode ? 16 : 22,
          }}>
            {message}
          </div>
        )}

        {inputMode && (
          <label style={{ display: 'block', marginBottom: 22 }}>
            <div style={{
              fontSize: 12, fontWeight: 600,
              color: 'var(--text-2)',
              marginBottom: 6,
            }}>
              {inputLabel}{inputRequired && <span style={{ color: 'var(--danger)' }}> *</span>}
            </div>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={inputPlaceholder}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                color: 'var(--text)',
                fontSize: 14,
                fontFamily: 'inherit',
                outline: 'none',
                resize: 'vertical',
                minHeight: 80,
                boxSizing: 'border-box',
              }}
            />
          </label>
        )}

        {/* Action row */}
        <div style={{
          display: 'flex',
          gap: 8,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          justifyContent: 'flex-end',
        }}>
          <Btn
            variant="outline"
            size="md"
            onClick={() => onCancel?.()}
            style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
          >
            {cancelLabel}
          </Btn>
          <Btn
            variant={confirmVariant}
            size="md"
            onClick={() => onConfirm?.(inputMode ? inputValue : undefined)}
            disabled={confirmDisabled}
            style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
          >
            <span ref={confirmBtnRef}>{confirmLabel}</span>
          </Btn>
        </div>
      </div>
    </div>
  );
}
